import type { FilterFieldDef, FilterSearchParams, FilterValues } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFields = Record<string, FilterFieldDef<any>>

export interface RawChip {
    key: string
    label: string | undefined
    onRemove: () => void
}

interface DefineOptions<TValues> {
    /**
     * Quy tắc chéo field (vd ngày kết thúc không nhỏ hơn ngày bắt đầu), chạy
     * sau khi parse. Cố ý chỉ một hook duy nhất thay vì đẻ ra rules engine —
     * quan hệ giữa các field là chuyện của từng màn, không tổng quát hóa được.
     */
    normalize?: (values: TValues) => TValues
}

export interface ListFiltersDef<T extends AnyFields> {
    fields: T
    defaults: FilterValues<T>
    /** Dùng cho `validateSearch` của TanStack Router. */
    validateSearch: (search: Record<string, unknown>) => FilterSearchParams
    /** URL → giá trị dùng được. Parse lại khi đọc nên rác không lọt vào control. */
    readValues: (search: Record<string, unknown>) => FilterValues<T>
    /** Giá trị → search params, chỉ giữ key khác mặc định. */
    toSearch: (values: FilterValues<T>) => FilterSearchParams
    /** Những key đang khác mặc định — dùng cho badge đếm (R3). */
    activeKeys: (values: FilterValues<T>) => (keyof T)[]
    /**
     * Chip bộ lọc đang bật, nhãn suy ra từ field (R12).
     * `keys` giới hạn tập field — mặc định lấy hết field đang khác mặc định.
     * `label` có thể `undefined` với field lọc theo id: nhãn phải tra runtime,
     * người gọi tự quyết bỏ chip hay thay nhãn khác.
     */
    buildChips: (
        values: FilterValues<T>,
        onApply: (next: FilterValues<T>) => void,
        keys?: (keyof T)[],
    ) => RawChip[]
}

const serializeValue = <TValue>(
    field: FilterFieldDef<TValue>,
    value: TValue,
) =>
    field.serialize
        ? field.serialize(value)
        : value === '' || value === undefined || value === null
          ? undefined
          : String(value)

/**
 * Khai bộ lọc của một màn danh sách một lần, suy ra mọi thứ còn lại: parser cho
 * URL, giá trị mặc định, số bộ lọc đang bật, chip, và cách ghi ngược lên URL.
 *
 * Trước đây mỗi màn tự viết ~44 dòng cho đúng những việc này, 27 màn là ~1.100
 * dòng gần giống hệt nhau — và mỗi màn tra nhãn chip theo một kiểu.
 */
export function defineListFilters<T extends AnyFields>(
    fields: T,
    options: DefineOptions<FilterValues<T>> = {},
): ListFiltersDef<T> {
    const keys = Object.keys(fields) as (keyof T & string)[]

    const defaults = keys.reduce(
        (acc, key) => {
            acc[key] = fields[key].defaultValue
            return acc
        },
        {} as Record<string, unknown>,
    ) as FilterValues<T>

    const parsePage = (raw: unknown) => {
        const page = typeof raw === 'number' ? raw : Number(raw ?? NaN)
        // `?page=2.7` sửa tay được; làm tròn xuống thay vì đẩy số lẻ xuống API.
        return Number.isFinite(page) && page > 1 ? Math.floor(page) : undefined
    }

    const readValues = (search: Record<string, unknown>) => {
        const out = keys.reduce(
            (acc, key) => {
                const parsed = fields[key].parse(search[key])
                acc[key] =
                    parsed === undefined ? fields[key].defaultValue : parsed
                return acc
            },
            {} as Record<string, unknown>,
        ) as FilterValues<T>
        return options.normalize ? options.normalize(out) : out
    }

    const toSearch = (values: FilterValues<T>) => {
        const out: FilterSearchParams = {}
        for (const key of keys) {
            const value = values[key]
            if (value === defaults[key]) continue
            const raw = serializeValue(fields[key], value)
            if (raw === undefined) continue
            out[key] = raw
        }
        return out
    }

    const activeKeys = (values: FilterValues<T>) =>
        keys.filter((key) => values[key] !== defaults[key])

    return {
        fields,
        defaults,
        validateSearch: (search) => {
            const out = toSearch(readValues(search))
            const page = parsePage(search.page)
            if (page !== undefined) out.page = page
            return out
        },
        readValues,
        toSearch,
        activeKeys,
        buildChips: (values, onApply, requestedKeys) =>
            (requestedKeys ?? activeKeys(values)).map((key) => {
                const field = fields[key]
                const value = values[key]
                return {
                    key: String(key),
                    // Không có chipLabel riêng → ghép nhãn field với giá trị
                    // đã serialize. Field lọc theo id nên tự khai chipLabel
                    // hoặc để màn truyền qua `chipLabels`.
                    label: field.chipLabel
                        ? field.chipLabel(value)
                        : `${field.label()}: ${serializeValue(field, value) ?? ''}`,
                    onRemove: () =>
                        onApply({ ...values, [key]: field.defaultValue }),
                }
            }),
    }
}
